import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCamera } from "components/Camera";
import { getConfig } from "config";
import { openDoor } from "hooks/useOpenDoor";
import { isVirtualContextlessDropoffEnabled } from "hooks/useVirtualContextlessDropoff";
import { beginCriticalOperation } from "services/criticalOperation";
import {
  readCustomerDepositRetry,
  saveCustomerDepositRetry,
  clearCustomerDepositRetry,
} from "services/customerDepositRetry";
import {
  fingerprintCustomerScan,
  isCustomerScanUnresolved,
  markCustomerScanUnresolved,
  clearCustomerScanUnresolved,
  isReservationDepositUnresolved,
  UnresolvedCustomerDepositError,
} from "services/unresolvedCustomerDeposits";
import {
  ContextlessDropoffError,
  createContextlessDropoff,
  createContextlessDropoffIdempotencyKey,
} from "services/contextlessDropoff";
import {
  fetchCustomerScanReservation,
  isTrackingBarcode,
  lookupCustomerScan,
} from "services/customerScan";
import { fetchCarrierValidationLockerCapability, updateReservation } from "services/lockerApi";

export type CustomerDepositState =
  | {
      status:
        | "idle"
        | "checking"
        | "submitting"
        | "found"
        | "manual_verification"
        | "interrupted"
        | "error";
    }
  | { status: "retry"; reason: "ambiguous" | "rate_limited" | "temporary" }
  | { status: "success"; alreadyDeposited: boolean };

interface CustomerDepositContextValue {
  state: CustomerDepositState;
  active: boolean;
  locked: boolean;
  start: (scan: string) => Promise<void>;
  retry: () => Promise<void>;
  restart: () => void;
  showInterrupted: () => void;
}

const CustomerDepositContext = createContext<CustomerDepositContextValue | null>(null);
const ROUTE = "/customer-deposit";
async function observe<T>(operation: Promise<T>, timeoutMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error("Deposit outcome is uncertain")), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

export function CustomerDepositProvider({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const location = useLocation();
  const camera = useCamera();
  const [state, setState] = useState<CustomerDepositState>({ status: "idle" });
  const stateRef = useRef(state);
  const pendingScan = useRef<string | null>(null);
  const pair = useRef<{
    scan: string;
    fingerprint: string;
    idempotencyKey: string;
    doorCommandStarted: boolean;
  } | null>(null);
  const controller = useRef<AbortController | null>(null);
  const progressController = useRef<AbortController | null>(null);
  const requestGeneration = useRef(0);
  const mounted = useRef(true);
  const release = useRef<(() => void) | null>(null);

  const transition = useCallback((next: CustomerDepositState) => {
    stateRef.current = next;
    if (mounted.current) setState(next);
  }, []);
  const hold = useCallback(() => {
    if (!release.current) release.current = beginCriticalOperation();
  }, []);
  const releaseOperation = useCallback(() => {
    release.current?.();
    release.current = null;
  }, []);
  const finish = useCallback(
    (next: CustomerDepositState) => {
      const retryCleared =
        !pair.current ||
        (next.status === "success" && clearCustomerDepositRetry(pair.current.fingerprint));
      pair.current = null;
      // A retained Admin receipt can authorize another local opening. Keep the
      // door claim unless retry-token removal was confirmed independently.
      if (pendingScan.current && retryCleared) clearCustomerScanUnresolved(pendingScan.current);
      pendingScan.current = null;
      transition(next);
      releaseOperation();
    },
    [releaseOperation, transition]
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
      progressController.current?.abort();
      requestGeneration.current += 1;
      pair.current = null;
      releaseOperation();
    };
  }, [hold, releaseOperation]);

  useEffect(() => {
    if (["manual_verification", "interrupted"].includes(state.status)) releaseOperation();
  }, [state.status, releaseOperation]);

  const active = state.status !== "idle";
  const locked = [
    "checking",
    "submitting",
    "found",
    "retry",
    "manual_verification",
    "interrupted",
  ].includes(state.status);

  useEffect(() => {
    // The provider survives animated routes, browser Back, and Home remounts.
    if (active && location.pathname !== ROUTE) navigate(ROUTE, { replace: true });
    else if (!active && location.pathname === ROUTE) navigate("/", { replace: true });
  }, [active, location.pathname, navigate]);

  useEffect(() => {
    if (!["submitting", "found", "retry"].includes(state.status)) return;
    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [state.status]);

  const submitPair = useCallback(async () => {
    const attempt = pair.current;
    if (!attempt || attempt.doorCommandStarted) return;
    const generation = ++requestGeneration.current;
    const progress = new AbortController();
    progressController.current = progress;
    transition({ status: "submitting" });
    try {
      const result = await createContextlessDropoff({
        scan: attempt.scan,
        idempotencyKey: attempt.idempotencyKey,
        progressSignal: progress.signal,
        onProgress: (phase) => {
          if (
            phase === "found" &&
            mounted.current &&
            !progress.signal.aborted &&
            requestGeneration.current === generation &&
            pair.current === attempt &&
            stateRef.current.status === "submitting"
          )
            transition({ status: "found" });
        },
      });
      if (!mounted.current || requestGeneration.current !== generation || pair.current !== attempt)
        return;
      progress.abort();
      if (result.status === "already_deposited") {
        // A pre-existing deposit is not permission to reopen its door.
        pair.current = null;
        transition({ status: "manual_verification" });
        return;
      }
      transition({ status: "found" });
      // Claim local dispatch before calling Warden. Any failure from this point
      // requires reconciliation; neither the POST nor the door may be retried.
      await markCustomerScanUnresolved(attempt.fingerprint, result.reservation_id);
      pendingScan.current = attempt.fingerprint;
      if (
        !mounted.current ||
        requestGeneration.current !== generation ||
        pair.current !== attempt
      ) {
        clearCustomerScanUnresolved(attempt.fingerprint);
        pendingScan.current = null;
        return;
      }
      attempt.doorCommandStarted = true;
      const opened = await observe(openDoor(result.door_position), 10000);
      if (!mounted.current || requestGeneration.current !== generation || pair.current !== attempt)
        return;
      if (!opened) throw new Error("Door outcome is uncertain");
      finish({ status: "success", alreadyDeposited: false });
    } catch (error) {
      if (!mounted.current || requestGeneration.current !== generation || pair.current !== attempt)
        return;
      if (attempt.doorCommandStarted) {
        pair.current = null;
        transition({ status: "manual_verification" });
      } else if (error instanceof UnresolvedCustomerDepositError) {
        pair.current = null;
        transition({ status: "interrupted" });
      } else if (error instanceof ContextlessDropoffError && error.recovery === "safe_exit") {
        finish({ status: "error" });
      } else if (
        error instanceof ContextlessDropoffError &&
        error.recovery === "manual_door_verification"
      ) {
        // Older server versions may have dispatched a door command themselves.
        try {
          await markCustomerScanUnresolved(attempt.fingerprint);
          pendingScan.current = attempt.fingerprint;
        } catch {
          // No local command is permitted when quarantine cannot be persisted.
        } finally {
          // Keep the original retry identity even if recovery storage fails.
          pair.current = null;
          transition({ status: "manual_verification" });
        }
      } else {
        transition({
          status: "retry",
          reason:
            error instanceof ContextlessDropoffError && error.status === 429
              ? "rate_limited"
              : error instanceof ContextlessDropoffError &&
                  ["timeout", "network"].includes(error.kind)
                ? "ambiguous"
                : "temporary",
        });
      }
    } finally {
      progress.abort();
      if (progressController.current === progress) progressController.current = null;
    }
  }, [finish, transition]);

  const depositKnown = useCallback(
    async (reservation: Awaited<ReturnType<typeof fetchCustomerScanReservation>>, scan: string) => {
      // PATCH does not dispatch a door. A later scan rechecks eligibility rather
      // than blindly repeating a PATCH whose response may have been lost.
      if (await isReservationDepositUnresolved(reservation)) {
        transition({ status: "interrupted" });
        return;
      }
      if (!mounted.current) return;
      transition({ status: "found" });
      let doorCommandStarted = false;
      try {
        const photo = camera.isEnabled ? camera.capture() : undefined;
        await observe(
          updateReservation({
            id: reservation.id,
            deposit: true,
            ...(photo ? { deposit_photo: photo } : {}),
          }),
          15000
        );
        if (!mounted.current) return;
        await markCustomerScanUnresolved(scan, reservation.id);
        pendingScan.current = scan;
        if (!mounted.current) {
          clearCustomerScanUnresolved(scan);
          pendingScan.current = null;
          return;
        }
        doorCommandStarted = true;
        const opened = await observe(openDoor(reservation.door.position), 10000);
        if (!mounted.current) return;
        if (!opened) throw new Error("Door outcome is uncertain");
        finish({ status: "success", alreadyDeposited: false });
      } catch {
        if (!mounted.current) return;
        if (doorCommandStarted) transition({ status: "manual_verification" });
        else finish({ status: "error" });
      }
    },
    [camera, finish, transition]
  );

  const start = useCallback(
    async function startDeposit(scan: string): Promise<void> {
      if (stateRef.current.status !== "idle") return;
      hold();
      transition({ status: "checking" });
      navigate(ROUTE);
      if (!isTrackingBarcode(scan) || !navigator.onLine) {
        finish({ status: "error" });
        return;
      }
      const abort = new AbortController();
      controller.current = abort;
      const deadline = window.setTimeout(() => abort.abort(), 15000);
      try {
        const fingerprint = await fingerprintCustomerScan(scan);
        if (!mounted.current) return;
        if (isCustomerScanUnresolved(fingerprint)) {
          transition({ status: "interrupted" });
          return;
        }
        const retryKey = readCustomerDepositRetry(fingerprint);
        const lookup = retryKey ? null : await lookupCustomerScan(scan, abort.signal);
        if (!mounted.current) return;
        if (lookup?.status === "found") {
          if (!lookup.can_deposit) throw new Error("Existing reservation is not eligible");
          const reservation = await fetchCustomerScanReservation(
            lookup.reservation_id,
            abort.signal
          );
          if (!mounted.current) return;
          window.clearTimeout(deadline);
          await depositKnown(reservation, fingerprint);
          return;
        }
        const locker = await fetchCarrierValidationLockerCapability({ signal: abort.signal });
        if (!mounted.current) return;
        if (
          locker?.id !== getConfig().lockerId ||
          !Array.isArray(locker.flags) ||
          !locker.flags.every((flag) => typeof flag === "string") ||
          (locker.offline !== undefined && locker.offline !== false) ||
          !isVirtualContextlessDropoffEnabled(locker as Pick<Locker, "flags">)
        )
          throw new Error("Customer contextless dropoff unavailable");
        window.clearTimeout(deadline);
        if (retryKey && readCustomerDepositRetry(fingerprint) !== retryKey) {
          // Capability may have taken us past expiry. Re-run lookup rather than
          // renewing the old key or assuming the parcel is still absent.
          transition({ status: "idle" });
          await startDeposit(scan);
          return;
        }
        const idempotencyKey = retryKey ?? createContextlessDropoffIdempotencyKey(locker.id);
        if (!retryKey) saveCustomerDepositRetry(fingerprint, idempotencyKey);
        if (!mounted.current) return;
        pair.current = {
          scan,
          fingerprint,
          idempotencyKey,
          doorCommandStarted: false,
        };
        await submitPair();
      } catch (error) {
        if (mounted.current) {
          if (error instanceof UnresolvedCustomerDepositError)
            transition({ status: "interrupted" });
          else finish({ status: "error" });
        }
      } finally {
        window.clearTimeout(deadline);
        controller.current = null;
      }
    },
    [depositKnown, finish, hold, navigate, submitPair, transition]
  );

  const retry = useCallback(async () => {
    if (stateRef.current.status !== "retry" || !pair.current || pair.current.doorCommandStarted)
      return;
    const attempt = pair.current;
    try {
      if (readCustomerDepositRetry(attempt.fingerprint) === attempt.idempotencyKey) {
        await submitPair();
      } else {
        // An expired attempt starts with fresh eligibility checks, never a blind POST.
        pair.current = null;
        transition({ status: "idle" });
        await start(attempt.scan);
      }
    } catch {
      finish({ status: "error" });
    }
  }, [finish, start, submitPair, transition]);

  const showInterrupted = useCallback(() => {
    if (stateRef.current.status !== "idle") return;
    // Other deposit entry points share the same recovery view without starting
    // a customer attempt or changing any unresolved parcel records.
    transition({ status: "interrupted" });
    navigate(ROUTE, { replace: true });
  }, [navigate, transition]);

  const restart = useCallback(() => {
    if (["checking", "submitting", "found"].includes(stateRef.current.status)) return;
    // Keep the pre-door retry key or uncertain-door fingerprint through Home.
    // A rescan can replay only the former; the latter still requires verification.
    pair.current = null;
    pendingScan.current = null;
    transition({ status: "idle" });
    navigate("/", { replace: true });
    releaseOperation();
  }, [navigate, releaseOperation, transition]);

  useEffect(() => {
    if (!["error", "success", "retry", "manual_verification", "interrupted"].includes(state.status))
      return;
    let timer: ReturnType<typeof setTimeout>;
    const resetTimeout = () => {
      clearTimeout(timer);
      timer = setTimeout(restart, 60000);
    };
    resetTimeout();
    document.addEventListener("pointerdown", resetTimeout);
    document.addEventListener("keydown", resetTimeout);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointerdown", resetTimeout);
      document.removeEventListener("keydown", resetTimeout);
    };
  }, [restart, state.status]);

  return (
    <CustomerDepositContext.Provider
      value={{ state, active, locked, start, retry, restart, showInterrupted }}>
      {children}
    </CustomerDepositContext.Provider>
  );
}

export function useCustomerDeposit() {
  const context = useContext(CustomerDepositContext);
  if (!context) throw new Error("useCustomerDeposit must be used within CustomerDepositProvider");
  return context;
}
